// src/components/dealer/VehicleCsvUploadModal.js
import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { dealerVehiclesAPI } from '../../services/api';

const VehicleCsvUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file.');
        setFile(null);
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) {
        // 5MB limit
        setError('File size must be less than 5MB.');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setUploadSuccess(false);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const { data } = await dealerVehiclesAPI.uploadCsv(file);

      setUploadSuccess(true);
      setUploadResult(data);
      setFile(null);

      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) fileInput.value = '';

      // Call success callback
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      console.error('Upload failed:', err);

      // Check if it's a 404 (endpoint not implemented)
      const is404 = err.response?.status === 404;

      if (is404) {
        setError(
          'CSV upload endpoint is not yet implemented.\n\n' +
          'To enable this feature, implement the POST /api/dealer/vehicles/import endpoint in your backend.\n\n' +
          'Expected functionality:\n' +
          '• Accept multipart/form-data with a CSV file\n' +
          '• Parse and validate vehicle data\n' +
          '• Create or update vehicles in the database\n' +
          '• Return import statistics (created, updated, errors)'
        );
      } else {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to upload CSV. Please check the file format and try again.';
        setError(errorMessage);

        // If validation errors exist, display them
        if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          const errorList = err.response.data.errors.slice(0, 5).join('\n');
          setError(`${errorMessage}\n\nErrors:\n${errorList}`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setError('');
      setUploadSuccess(false);
      setUploadResult(null);
      onClose();
    }
  };

  const csvExample = `year,make,model,trim,price,mileage,vin,availableForTestDrive
2024,Toyota,Camry,XLE,32500,5200,1HGBH41JXMN109186,true
2023,Honda,Accord,Sport,28900,12400,1HGCV1F16LA123456,true
2022,Ford,F-150,Lariat,45000,28000,1FTFW1E85MFC12345,false
2024,Tesla,Model 3,Long Range,48990,1500,5YJ3E1EA1LF123456,true`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-app-border-light dark:border-app-border">
          <h2 className="text-lg font-semibold text-app-text-light dark:text-app-text">
            Upload Vehicle Inventory
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Instructions */}
          <div className="bg-app-bg-light dark:bg-app-bg rounded-xl p-4 border border-app-border-light dark:border-app-border">
            <div className="flex items-start gap-2 mb-2">
              <FileText className="w-4 h-4 text-app-accent mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-app-text-light dark:text-app-text mb-1">
                  CSV Format Instructions
                </h3>
                <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-2">
                  Your CSV file should include the following columns:
                </p>
                <ul className="text-xs text-app-text-secondary-light dark:text-app-text-secondary space-y-1 list-disc list-inside">
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">year</strong> - Vehicle year
                    (e.g., 2024)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">make</strong> - Manufacturer
                    (e.g., Toyota)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">model</strong> - Model name
                    (e.g., Camry)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">trim</strong> - Trim level
                    (optional, e.g., XLE)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">price</strong> - Price in dollars
                    (e.g., 32500)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">mileage</strong> - Mileage in
                    miles (e.g., 5200)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">vin</strong> - Vehicle
                    Identification Number (optional)
                  </li>
                  <li>
                    <strong className="text-app-text-light dark:text-app-text">availableForTestDrive</strong> -
                    true or false (default: true)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Example CSV */}
          <div className="bg-app-bg-light dark:bg-app-bg rounded-xl p-4 border border-app-border-light dark:border-app-border">
            <h3 className="text-sm font-semibold text-app-text-light dark:text-app-text mb-2">
              Example CSV Format
            </h3>
            <pre className="text-xs text-app-text-secondary-light dark:text-app-text-secondary bg-app-bg-quaternary-light dark:bg-app-bg-quaternary rounded-lg p-3 overflow-x-auto font-mono">
              {csvExample}
            </pre>
          </div>

          {/* File Upload */}
          <div className="space-y-3">
            <div className="border-2 border-dashed border-app-border-light dark:border-app-border rounded-xl p-6 text-center hover:border-app-accent transition-colors">
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
              />
              <label
                htmlFor="csv-file-input"
                className={`cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload className="w-8 h-8 text-app-text-secondary-light dark:text-app-text-secondary mx-auto mb-2" />
                <div className="text-sm text-app-text-light dark:text-app-text mb-1">
                  {file ? file.name : 'Click to select a CSV file'}
                </div>
                <div className="text-xs text-app-text-muted-light dark:text-app-text-muted">
                  Maximum file size: 5MB
                </div>
              </label>
            </div>

            {file && !uploadSuccess && (
              <div className="flex items-center gap-2 text-xs text-app-text-light dark:text-app-text bg-app-bg-light dark:bg-app-bg rounded-lg p-3 border border-app-border-light dark:border-app-border">
                <FileText className="w-4 h-4 text-app-accent" />
                <span className="flex-1">{file.name}</span>
                <span className="text-app-text-muted-light dark:text-app-text-muted">
                  {(file.size / 1024).toFixed(2)} KB
                </span>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-xs text-red-300 whitespace-pre-wrap">{error}</div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {uploadSuccess && uploadResult && (
            <div className="bg-green-900/30 border border-green-700/50 rounded-xl p-3 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-green-300 mb-1">
                  Upload Successful!
                </div>
                <div className="text-xs text-green-400">
                  {uploadResult.imported || uploadResult.created || 0} vehicles
                  imported,{' '}
                  {uploadResult.updated || 0} vehicles updated
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-app-border-light dark:border-app-border">
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 rounded-xl bg-app-bg-tertiary-light dark:bg-app-bg-tertiary text-app-text-light dark:text-app-text text-sm font-semibold hover:bg-app-border-light dark:hover:bg-app-border transition-colors disabled:opacity-50"
          >
            {uploadSuccess ? 'Close' : 'Cancel'}
          </button>
          {!uploadSuccess && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 rounded-xl bg-app-accent text-black text-sm font-semibold hover:bg-app-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleCsvUploadModal;
